import { useState, useCallback, useMemo } from 'react';
import { useMtlsProducts } from '../../hooks/useMtlsData';
import { useFilterState } from '../../hooks/useFilterState';
import { ApiService } from '../../services/api';
import { 
  SearchFilter, 
  ConfirmationModal,
} from './shared';
import { AdvancedFilter } from './shared/AdvancedFilter';
import { UniversalSchemaForm } from '../shared/UniversalSchemaForm';
import { UniversalSchemaTable } from '../shared/UniversalSchemaTable';
import { MTLS_OPENAPI } from '../../schemas';
import { 
  handleApiError, 
  handleApiSuccess 
} from '../../utils/mtls-error-handler';
import { filterEmptyOptionalFields } from '../../utils/api-helpers';
import { 
  MtlsProduct,
  CreateProductRequest,
  UpdateProductRequest 
} from '../../types/mtls';
import { ProductEnvironmentPanel } from './ProductEnvironmentPanel';

export function ProductManagement(): JSX.Element {
  const {
    filters,
    updateFilters,
    resetFilters,
  } = useFilterState({
    initialFilters: { page: 1, size: 10 },
    persistKey: 'products',
  });
  
  // Type-safe filters for the API call - memoized to prevent infinite loops
  const productFilters = useMemo(() => ({
    page: filters.page as number,
    size: filters.size as number,
    name: filters.search as string,
    organizationId: filters.organizationId as number,
  }), [filters.page, filters.size, filters.search, filters.organizationId]);
  
  const { products, loading, error, reload } = useMtlsProducts(productFilters);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedProductForEnvironments, setSelectedProductForEnvironments] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSearch = useCallback((search: string) => {
    updateFilters({ search, page: 1 });
  }, [updateFilters]);

  const handleFilterChange = useCallback((key: string, value: string) => {
    updateFilters({ [key]: value === '' ? undefined : (key === 'organizationId' ? parseInt(value) : value), page: 1 });
  }, [updateFilters]);

  const handleAdvancedFilterChange = useCallback((key: string, value: any) => {
    updateFilters({ [key]: value, page: 1 });
  }, [updateFilters]);

  const handleCreateProduct = () => {
    setSelectedProduct(null);
    setShowCreateModal(true);
  };

  const handleEditProduct = (product: MtlsProduct) => {
    setSelectedProduct(product);
    setShowCreateModal(true);
  };

  const handleDeleteProduct = (product: MtlsProduct) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const handleManageEnvironments = (product: MtlsProduct) => {
    setSelectedProductForEnvironments(product);
  };

  const handleProductFormSubmit = async (data: Record<string, any>) => {
    const isEdit = !!selectedProduct;
    
    // Filter out empty optional fields before sending to API
    const filteredData = filterEmptyOptionalFields(data) as CreateProductRequest | UpdateProductRequest;

    setSubmitting(true);
    try {
      if (isEdit && selectedProduct) {
        await ApiService.updateMtlsProduct(selectedProduct.id, filteredData as UpdateProductRequest);
        handleApiSuccess('Product updated successfully', 'Product Management');
        setShowCreateModal(false);
      } else {
        await ApiService.createMtlsProduct(filteredData as CreateProductRequest);
        handleApiSuccess('Product created successfully', 'Product Management');
        setShowCreateModal(false);
      }
      
      reload();
    } catch (error) {
      handleApiError(error, 'Product Management');
    } finally {
      setSubmitting(false);
    }
  };

  const handleProductDeleteConfirm = async () => {
    if (!selectedProduct) return;

    setSubmitting(true);
    try {
      await ApiService.deleteMtlsProduct(selectedProduct.id);
      handleApiSuccess('Product deleted successfully', 'Product Management');
      setShowDeleteModal(false);
      reload();
    } catch (error) {
      handleApiError(error, 'Product Management');
    } finally {
      setSubmitting(false);
    }
  };

  const basicFilterOptions: any[] = [];

  const advancedFilterConfig = [
    {
      key: 'environmentCount',
      label: 'Minimum Environment Count',
      type: 'number' as const,
      min: 0,
      placeholder: 'Minimum number of environments',
    },
    {
      key: 'hasNotifications',
      label: 'Has Notifications Enabled',
      type: 'boolean' as const,
      placeholder: 'Filter by notification status',
    },
    {
      key: 'createdDateRange',
      label: 'Created Date Range',
      type: 'daterange' as const,
    },
  ];

  if (error) {
    return (
      <div className="alert alert-danger">
        <h4><i className="icon fas fa-ban"></i> Error!</h4>
        Failed to load products: {error}
        <div className="mt-2">
          <button className="btn btn-outline-danger" onClick={reload}>
            <i className="fas fa-redo mr-2"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-management">
      {/* Header */}
      <div className="row mb-3">
        <div className="col-sm-6">
          <h3>
            <i className="fas fa-cube mr-2"></i>
            Products & Environments
          </h3>
        </div>
        <div className="col-sm-6 text-right">
          <button
            className="btn btn-primary"
            onClick={handleCreateProduct}
          >
            <i className="fas fa-plus mr-2"></i>
            New Product
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card card-outline card-primary">
        <div className="card-body">
          <SearchFilter
            searchPlaceholder="Search products by name or description..."
            searchValue={filters.search}
            onSearchChange={handleSearch}
            filters={basicFilterOptions}
            onFilterChange={handleFilterChange}
            onClear={resetFilters}
          />
          
          <AdvancedFilter
            filters={advancedFilterConfig}
            values={filters}
            onChange={handleAdvancedFilterChange}
            onReset={resetFilters}
            className="mt-3"
          />
        </div>
      </div>

      {/* Products Table */}
      <div className="card card-outline card-primary">
        <div className="card-body">
          <UniversalSchemaTable
            openApiSpec={MTLS_OPENAPI}
            schemaName="Product"
            data={products}
            loading={loading}
            emptyMessage="No products found. Create your first product to get started."
            onView={undefined} // We'll add a view modal later if needed
            onEdit={handleEditProduct}
            onDelete={handleDeleteProduct}
            customActions={[
              {
                label: 'Manage Environments',
                icon: 'fas fa-cogs',
                onClick: handleManageEnvironments
              }
            ]}
            hiddenFields={[
              'id', // Hide internal ID
              'createdBy', // Hide internal fields
              'updatedBy',
              'updatedAt'
            ]}
            customLabels={{
              name: 'Product Name',
              organizationId: 'Organization ID',
              organizationName: 'Organization',
              environmentCount: 'Environments',
              status: 'Status',
              createdAt: 'Created',
              description: 'Description'
            }}
            customColumnTypes={{
              status: 'badge',
              environmentCount: 'badge',
              createdAt: 'datetime',
              updatedAt: 'datetime'
            }}
            striped={true}
            hover={true}
            responsive={true}
          />
        </div>
      </div>

      {/* Environment Management Panel */}
      {selectedProductForEnvironments && (
        <ProductEnvironmentPanel
          product={selectedProductForEnvironments}
          onClose={() => setSelectedProductForEnvironments(null)}
        />
      )}

      {/* Product Form Modals */}
      {/* Create Product Modal */}
      {showCreateModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    {selectedProduct ? 'Edit Product' : 'Create Product'}
                  </h4>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setShowCreateModal(false)}
                    disabled={submitting}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <UniversalSchemaForm
                    openApiSpec={MTLS_OPENAPI}
                    schemaName={selectedProduct ? 'UpdateProductRequest' : 'CreateProductRequest'}
                    onSubmit={handleProductFormSubmit}
                    initialData={selectedProduct ? {
                      name: selectedProduct.name || '',
                      description: selectedProduct.description || '',
                      notificationEmail: selectedProduct.notificationEmail || '',
                      notificationContactName: selectedProduct.notificationContactName || '',
                    } : {}}
                    loading={submitting}
                    showSubmitButton={false}
                    formId={`product-form-${selectedProduct ? 'edit' : 'create'}`}
                    customLabels={{
                      name: 'Product Name',
                      description: 'Description',
                      notificationEmail: 'Notification Email',
                      notificationContactName: 'Notification Contact Name'
                    }}
                    customPlaceholders={{
                      name: 'e.g., My API Product',
                      description: 'e.g., API for customer data management',
                      notificationEmail: 'e.g., notifications@example.com',
                      notificationContactName: 'e.g., John Smith'
                    }}
                    customPatterns={{
                      notificationEmail: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
                    }}
                  />
                </div>
                <div className="modal-footer justify-content-between">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => setShowCreateModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form={`product-form-${selectedProduct ? 'edit' : 'create'}`}
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting && (
                      <span className="spinner-border spinner-border-sm mr-2" role="status">
                        <span className="sr-only">Loading...</span>
                      </span>
                    )}
                    <i className="fas fa-save mr-2"></i>
                    {selectedProduct ? 'Update Product' : 'Create Product'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Product Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Product"
        message={`Are you sure you want to delete "${selectedProduct?.name || selectedProduct?.productName}"? This action cannot be undone and will also delete all associated environments and certificate assignments.`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleProductDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
        loading={submitting}
      />
    </div>
  );
}